<template>
    <div class="home-view">
        <!-- Hos geldiniz + hizli erisim: yalin, template ile uyumlu. -->
        <b-row>
            <b-col lg="8">
                <b-card class="home-hero mb-2">
                    <div class="d-flex align-items-center justify-content-between flex-wrap">
                        <div class="mb-1 mb-md-0">
                            <h3 class="mb-25 font-weight-bolder">
                                Hoş geldiniz{{ userFullName ? ", " + userFullName : "" }} 👋
                            </h3>
                            <small class="text-muted">Vişne Madencilik · Lojistik Portal</small>
                        </div>
                        <div class="d-flex flex-wrap home-hero__actions">
                            <b-button variant="primary" v-on:click="go('agreement')" class="mr-50 mb-50">
                                <feather-icon icon="FileTextIcon" size="14" class="mr-25"/>
                                Sözleşmeler
                            </b-button>
                            <b-button variant="outline-primary" v-on:click="go('voyage')" class="mr-50 mb-50">
                                <feather-icon icon="TruckIcon" size="14" class="mr-25"/>
                                Seferler
                            </b-button>
                            <b-button variant="outline-primary" v-on:click="go('waybill-operations')" class="mb-50">
                                <feather-icon icon="ClipboardIcon" size="14" class="mr-25"/>
                                İrsaliye
                            </b-button>
                        </div>
                    </div>
                </b-card>
            </b-col>
            <b-col lg="4">
                <b-card class="mb-2 home-price-card">
                    <div class="d-flex align-items-center justify-content-between">
                        <div>
                            <small class="text-muted d-block">Güncel Mazot Fiyatı</small>
                            <h3 class="mb-0 font-weight-bolder">{{ currentPriceStr }} ₺</h3>
                            <small v-if="lastDate" class="text-muted">{{ lastDate }}</small>
                        </div>
                        <b-avatar variant="light-primary" size="45">
                            <span class="material-icons-round">local_gas_station</span>
                        </b-avatar>
                    </div>
                    <div v-if="priceChangeText" class="mt-1">
                        <b-badge :variant="priceChangeVariant">
                            <feather-icon :icon="priceChangeIcon" size="12" class="mr-25"/>
                            {{ priceChangeText }}
                        </b-badge>
                        <small class="text-muted ml-50">son 30 güne göre</small>
                    </div>
                </b-card>
            </b-col>
        </b-row>

        <!-- Mazot trend grafigi: son 30 gun. Sistemin ana veri kaynagi, yalin bir gostergesi. -->
        <b-card class="home-chart position-relative">
            <div class="d-flex align-items-center justify-content-between mb-1">
                <div>
                    <h4 class="mb-25 font-weight-bolder">Mazot Fiyat Trendi</h4>
                    <small class="text-muted">Son 30 gün</small>
                </div>
                <b-badge variant="light-primary">
                    {{ series.length }} nokta
                </b-badge>
            </div>
            <vue-apex-charts
                v-if="series.length"
                type="area"
                height="260"
                :options="chartOptions"
                :series="chartSeries"/>
            <div v-else-if="!loading" class="text-center text-muted py-3">
                <feather-icon icon="BarChart2Icon" size="32" class="mb-25"/>
                <div>Gösterilecek fiyat verisi bulunamadı.</div>
            </div>
            <v-overlay-loader :loading="loading" text="Fiyatlar yükleniyor"/>
        </b-card>
    </div>
</template>

<script>
import { BCard, BRow, BCol, BButton, BAvatar, BBadge } from "bootstrap-vue"
import VueApexCharts from "vue-apexcharts"
import Auth from "@/utilities/auth"
import { FuelPriceApi } from "@/api/fuel-price"
import moment from "moment"

export default {
    components: {
        BCard, BRow, BCol, BButton, BAvatar, BBadge,
        VueApexCharts
    },
    data(){
        return {
            loading: false,
            series: []
        }
    },
    computed: {
        // JWT'de FullName / FirstName mevcut; kullaniciya kisa hitap.
        userFullName(){
            const u = Auth.user
            if(!u) return ""
            return u.FullName || u.FirstName || u.Email || ""
        },
        currentPriceStr(){
            if(!this.series.length) return "—"
            const p = this.series[this.series.length - 1].price
            return p.toLocaleString("tr-TR", { minimumFractionDigits: 2 })
        },
        lastDate(){
            if(!this.series.length) return null
            return moment(this.series[this.series.length - 1].date).format("DD.MM.YYYY")
        },
        // Ilk ve son fiyat arasindaki degisim. Negatifse yesil (indi), pozitifse kirmizi (arttı).
        priceChangeText(){
            if(this.series.length < 2) return null
            const first = this.series[0].price
            const last = this.series[this.series.length - 1].price
            if(first === 0) return null
            const pct = ((last - first) / first) * 100
            const sign = pct > 0 ? "+" : ""
            return sign + pct.toFixed(2) + "%"
        },
        priceChangeVariant(){
            if(!this.priceChangeText) return "light"
            return this.priceChangeText.startsWith("-") ? "light-success" : "light-danger"
        },
        priceChangeIcon(){
            if(!this.priceChangeText) return "MinusIcon"
            return this.priceChangeText.startsWith("-") ? "TrendingDownIcon" : "TrendingUpIcon"
        },
        chartSeries(){
            return [{
                name: "Mazot",
                data: this.series.map(p => [ new Date(p.date).getTime(), p.price ])
            }]
        },
        chartOptions(){
            return {
                chart: {
                    type: "area",
                    zoom: { enabled: false },
                    toolbar: { show: false },
                    fontFamily: "inherit"
                },
                colors: ["#0F3D91"],
                dataLabels: { enabled: false },
                stroke: { curve: "smooth", width: 2 },
                fill: {
                    type: "gradient",
                    gradient: {
                        shadeIntensity: 1,
                        opacityFrom: 0.35,
                        opacityTo: 0.05,
                        stops: [0, 90, 100]
                    }
                },
                xaxis: {
                    type: "datetime",
                    labels: { datetimeUTC: false, format: "dd MMM" }
                },
                yaxis: {
                    labels: {
                        formatter: v => v ? v.toLocaleString("tr-TR", { minimumFractionDigits: 2 }) : "0"
                    }
                },
                tooltip: {
                    x: { format: "dd.MM.yyyy" },
                    y: { formatter: v => v.toLocaleString("tr-TR", { minimumFractionDigits: 2 }) + " ₺" }
                },
                grid: { borderColor: "#eef1f6" }
            }
        }
    },
    mounted(){
        this.load()
    },
    methods: {
        go(name){
            this.$router.push({ name }).catch(() => {})
        },
        load(){
            this.loading = true
            const to = moment().format("YYYY-MM-DD")
            const from = moment().subtract(30, "days").format("YYYY-MM-DD")
            FuelPriceApi.List({ fromDate: from, toDate: to }).then(res => {
                if(res.data && res.data.Success && Array.isArray(res.data.Data)){
                    // Servis DESC donduruyor; grafik icin tarih artan olmali.
                    this.series = res.data.Data
                        .map(x => ({ date: x.Date, price: Number(x.Price) || 0 }))
                        .sort((a, b) => new Date(a.date) - new Date(b.date))
                }
                this.loading = false
            }).catch(() => {
                this.loading = false
            })
        }
    }
}
</script>

<style lang="scss" scoped>
$brand-primary: #0F3D91;

.home-hero {
    background: linear-gradient(120deg, rgba(15, 61, 145, 0.06) 0%, rgba(15, 61, 145, 0.02) 60%, transparent 100%);
    h3 { color: $brand-primary; }
}

.home-price-card h3 {
    color: $brand-primary;
}

.home-hero__actions {
    max-width: 100%;
}

.home-chart {
    min-height: 300px;
}
</style>
